<template>
  <aside
    v-if="model.enabled && visible"
    class="team-voice-panel fixed right-3 z-40 w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-brand-blue-100 bg-white text-brand-brown shadow-xl"
    aria-label="GSE team audio"
  >
    <header class="flex items-center gap-3 bg-brand-yellow px-4 py-3">
      <Radio
        v-if="model.current?.mode === 'ptt' || model.review?.mode === 'ptt'"
        class="h-5 w-5 shrink-0"
      />
      <Phone v-else class="h-5 w-5 shrink-0" />
      <div class="min-w-0 flex-1">
        <h2 class="truncate text-sm font-semibold">
          {{ model.current?.label || model.review?.label || 'Your GSE team' }}
        </h2>
        <p class="text-xs">
          {{
            model.current ? phaseLabel : model.review ? 'Ready when you are' : 'Open with your team'
          }}
        </p>
      </div>
      <button
        v-if="model.current"
        class="min-h-11 rounded-lg px-2 text-xs font-semibold hover:bg-white/40"
        :aria-expanded="!collapsed"
        @click="collapsed = !collapsed"
      >
        {{ collapsed ? 'Open' : 'Minimize' }}
      </button>
    </header>
    <div v-if="!collapsed || !model.current" class="max-h-[65dvh] space-y-4 overflow-y-auto p-4">
      <p v-if="model.message" class="rounded-xl bg-brand-yellow-light p-3 text-sm" role="alert">
        {{ model.message }}
        <button class="ml-1 underline" @click="model.message = null">Dismiss</button>
      </p>
      <p v-if="model.loading" class="text-sm" role="status">Checking your conversation…</p>
      <section v-if="model.review" class="space-y-3" aria-label="Review team call">
        <p class="text-sm">
          {{
            model.review.mode === 'ptt'
              ? 'Join this walkie channel. Hold to talk when the channel is free.'
              : 'Talk freely with the people in this conversation.'
          }}
        </p>
        <ul class="space-y-1 text-sm">
          <li v-for="person in model.review.members" :key="person.uid">{{ person.name }}</li>
        </ul>
        <p class="text-xs text-slate-500">Your browser asks for microphone access when you join.</p>
        <div class="flex gap-2">
          <button
            class="min-h-11 flex-1 rounded-xl bg-brand-dark-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            :disabled="model.busy"
            @click="model.confirm()"
          >
            {{
              model.review.mode === 'ptt'
                ? 'Join walkie'
                : model.review.current
                  ? 'Join the call'
                  : 'Start voice call'
            }}
          </button>
          <button
            class="min-h-11 rounded-xl border border-slate-200 px-3 text-sm"
            @click="model.cancelReview()"
          >
            Cancel
          </button>
        </div>
      </section>
      <section v-if="model.current" class="space-y-3" aria-label="Current team call">
        <p class="text-sm" role="status">{{ phaseLabel }}</p>
        <ul class="space-y-2 text-sm">
          <li
            v-for="person in model.current.people"
            :key="person.uid"
            class="flex items-center justify-between gap-3"
          >
            <span class="truncate">{{ person.name }}</span
            ><span class="shrink-0 text-xs text-slate-500">{{ personLabel(person.phase) }}</span>
          </li>
        </ul>
        <button
          v-if="model.current.phase === 'uncertain'"
          class="min-h-11 w-full rounded-xl border border-brand-blue px-4 text-sm text-brand-dark-blue"
          @click="model.retry()"
        >
          Check this same call
        </button>
        <template v-if="model.current.phase === 'connected'">
          <button
            v-if="model.current.mode === 'open'"
            class="min-h-11 w-full rounded-xl border border-brand-blue px-4 text-sm text-brand-dark-blue"
            :aria-pressed="model.current.muted"
            @click="model.mute(!model.current.muted)"
          >
            {{ model.current.muted ? 'Unmute microphone' : 'Mute microphone' }}
          </button>
          <template v-else>
            <p class="text-sm font-semibold" aria-live="polite">
              {{
                model.current.transmitting
                  ? 'You have the channel'
                  : model.current.speaker
                    ? model.current.speaker.name + ' has the channel'
                    : 'The channel is free'
              }}
            </p>
            <button
              class="min-h-24 w-full touch-none select-none rounded-2xl px-4 py-5 text-lg font-semibold text-white"
              :class="model.current.transmitting ? 'bg-brand-brown' : 'bg-brand-dark-blue'"
              :aria-pressed="model.current.transmitting"
              :aria-label="
                model.current.transmitting
                  ? 'Stop talking to the crew'
                  : model.current.waitingForFloor
                    ? 'Stop waiting for the channel'
                    : 'Talk to the crew'
              "
              @pointerdown.prevent="beginPointer"
              @pointerup.prevent="model.release()"
              @pointercancel="model.release()"
              @lostpointercapture="model.release()"
              @keydown.space.prevent="beginKeyboard"
              @keyup.space.prevent="model.release()"
              @keydown.enter.prevent="beginKeyboard"
              @keyup.enter.prevent="model.release()"
              @blur="model.release()"
              @click="accessiblePress"
            >
              {{
                model.current.transmitting
                  ? 'Talking…'
                  : model.current.waitingForFloor
                    ? 'Waiting for the channel…'
                    : 'Hold to talk'
              }}
            </button>
            <p class="text-xs text-slate-500">
              Listen with the channel open. Hold the button or Space to speak; release to listen.
            </p>
          </template>
        </template>
        <button
          class="min-h-11 w-full rounded-xl border border-slate-200 px-4 text-sm font-semibold text-brand-brown"
          :disabled="model.current.phase === 'closing'"
          @click="model.leave()"
        >
          {{
            model.current.phase === 'closing'
              ? 'Closing audio…'
              : model.current.mode === 'ptt'
                ? 'Leave walkie'
                : 'Leave call'
          }}
        </button>
      </section>
      <section
        v-if="!model.current && !model.review && !model.loading && model.calls.length"
        class="space-y-3"
        aria-label="Team call invitations"
      >
        <article
          v-for="room in model.calls"
          :key="room.roomId"
          class="rounded-xl border border-brand-blue-100 p-3"
        >
          <h3 class="text-sm font-semibold">{{ room.label }}</h3>
          <p class="mb-2 text-xs text-slate-500">
            {{
              room.mode === 'ptt'
                ? 'Walkie channel'
                : room.ringing
                  ? 'Your team is calling…'
                  : 'Open voice call'
            }}
          </p>
          <div class="flex gap-2">
            <button
              class="min-h-11 flex-1 rounded-lg bg-brand-dark-blue px-3 text-sm font-semibold text-white"
              @click="model.join(room)"
            >
              Review and join</button
            ><button
              v-if="!room.claimId"
              class="min-h-11 rounded-lg border border-slate-200 px-3 text-sm"
              @click="model.decline(room)"
            >
              Decline
            </button>
          </div>
        </article>
      </section>
    </div>
  </aside>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { Phone, Radio } from 'lucide-vue-next'
const props = defineProps({ model: { type: Object, required: true } })
const model = computed(() => props.model),
  collapsed = ref(false)
const visible = computed(
  () =>
    model.value.current ||
    model.value.review ||
    model.value.loading ||
    model.value.message ||
    model.value.calls.length
)
const phaseLabel = computed(
  () =>
    ({
      preparing: 'Getting your call ready…',
      connecting: 'Connecting to your team…',
      connected:
        model.value.current?.mode === 'ptt' ? 'Walkie is open. Listening.' : 'You’re connected.',
      reconnecting: 'Reconnecting… Your microphone is paused.',
      uncertain: 'Waiting to confirm this call',
      closing: 'Closing the audio session…',
    })[model.value.current?.phase] || 'Your team'
)
function personLabel(phase) {
  return (
    {
      connected: 'Connected',
      connecting: 'Joining…',
      ringing: 'Ringing…',
      declined: 'Declined',
      leaving: 'Leaving…',
      left: 'Left',
      available: 'Available to join',
    }[phase] || 'Available'
  )
}
function beginPointer(event) {
  event.currentTarget.setPointerCapture?.(event.pointerId)
  model.value.press()
}
function beginKeyboard(event) {
  if (!event.repeat) model.value.press()
}
function accessiblePress(event) {
  if (event.detail === 0) {
    if (model.value.current?.transmitting || model.value.current?.waitingForFloor)
      model.value.release()
    else model.value.press()
  }
}
watch(
  () => model.value.current?.roomId,
  () => {
    collapsed.value = false
  }
)
onUnmounted(() => model.value.release())
</script>

<style scoped>
.team-voice-panel {
  bottom: calc(5.5rem + env(safe-area-inset-bottom));
}
@media (min-width: 1024px) {
  .team-voice-panel {
    bottom: 1rem;
  }
}
</style>
